import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../../store/slices/auth/slice";
import supportChatService from "../../services/supportChatService";
import {
  FiSend,
  FiX,
  FiMinimize2,
  FiMaximize2,
  FiUser,
  FiMessageCircle,
} from "react-icons/fi";
import {
  ChatContainer,
  ChatHeader,
  ChatTitle,
  ChatControls,
  ChatButton,
  ChatBody,
  MessagesContainer,
  Message,
  MessageContent,
  MessageTime,
  MessageSender,
  ChatInput,
  InputContainer,
  SendButton,
  WelcomeMessage,
  TypingIndicator,
  ChatFooter,
  StatusIndicator,
  ErrorMessage,
  ModalOverlay,
  ConfirmBox,
  ModalActions,
  Confirm,
  CloseBtn,
} from "./SupportChat.styles";

const SupportChat = ({ isOpen, onClose, onMinimize, isMinimized }) => {
  const user = useSelector(selectUser);
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: `Zdravo ${
        user?.username || ""
      }! Dobrodošli u korisničku podršku. Kako možemo da vam pomognemo danas?`,
      sender: "support",
      timestamp: new Date(),
    },
  ]);
  const [isTyping, setIsTyping] = useState(false);
  const [chatId, setChatId] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [adminOnline, setAdminOnline] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [showSessionEndModal, setShowSessionEndModal] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Initialize chat service when component mounts
  useEffect(() => {
    if (user && isOpen) {

      // Setup support chat service callbacks
      supportChatService.setCallbacks({
        onConnect: () => {
          setIsConnected(true);
          setConnectionError(null);

          // Check for existing session after connecting
          supportChatService.requestAdminStatus();
          supportChatService.checkExistingSession();
        },
        onDisconnect: () => {
          setIsConnected(false);
          setAdminOnline(false);
        },
        onMessage: handleIncomingMessage,
        onTyping: handleTypingIndicator,
        onSessionStarted: (data) => {
          setChatId(data.sessionId);
          supportChatService.requestAdminStatus();
        },
        onSessionEnded: handleSessionEnded,
        onExistingSessionFound: handleExistingSessionFound,
        onNoExistingSession: () => {
          // No existing session found
        },
        onAdminStatus: (data) => {
          const adminsOnline =
            data.adminsOnline || data.online || data.adminCount > 0 || false;
          setAdminOnline(adminsOnline);
        },
        onNoAdminsAvailable: (data) => {
          setAdminOnline(false);
          const fallbackMessage = {
            id: Date.now(),
            text: data.message,
            sender: "support",
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, fallbackMessage]);
        },
        onError: handleChatError,
      });

      // Connect to support chat service
      supportChatService.connect({
        id: user.id,
        userType: "band",
        username: user.username || user.name,
        email: user.email,
      });
    }

    return () => {
      if (isOpen && user) {
        supportChatService.disconnect();
      }
    };
  }, [user, isOpen]);

  useEffect(() => {
    let typingTimeout;

    if (isTyping) {
      // Automatski očisti typing nakon 3 sekunde
      typingTimeout = setTimeout(() => {
        setIsTyping(false);
      }, 3000);
    }

    return () => {
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }
    };
  }, [isTyping]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && !isMinimized && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, isMinimized]);

  useEffect(() => {
    let statusInterval;

    if (isConnected) {
      // Zatraži admin status svake minute
      statusInterval = setInterval(() => {
        supportChatService.requestAdminStatus();
      }, 60000);
    }

    return () => {
      if (statusInterval) {
        clearInterval(statusInterval);
      }
    };
  }, [isConnected]);

  // Handle incoming messages
  const handleIncomingMessage = (data) => {
    const newMessage = {
      id: data.message.id || Date.now(),
      text: data.message.text,
      sender: data.message.sender === "admin" || data.message.sender === "system" ? "support" : "user",
      timestamp: new Date(data.message.timestamp),
    };

    setMessages((prev) => [...prev, newMessage]);
  };

  // Handle typing indicator
  const handleTypingIndicator = (data) => {
    // Check if typing indicator is from admin user
    if (data.userType === "admin") {
      setIsTyping(data.isTyping);
    }
  };

  // Handle chat errors
  const handleChatError = (error) => {
    setConnectionError(error.message || error);
    console.error("Support chat error:", error);
  };

  // Handle existing session found
  const handleExistingSessionFound = (data) => {
    setChatId(data.sessionId);

    // Set messages from existing session if they exist
    if (data.messages && data.messages.length > 0) {
      const convertedMessages = data.messages.map((msg) => ({
        id: msg.id,
        text: msg.message || msg.text, // Handle different property names
        sender:
          msg.senderType === "admin" || msg.sender === "admin" || msg.sender === "system"
            ? "support"
            : "user",
        timestamp: new Date(msg.timestamp || msg.createdAt),
      }));

      setMessages([
        {
          id: 0,
          text: `Zdravo ${
            user?.username || user?.name || ""
          }! Dobrodošli u korisničku podršku. Kako možemo da vam pomognemo danas?`,
          sender: "support",
          timestamp: new Date(),
        },
        ...convertedMessages,
      ]);
    }
  };

  // Handle session ended by admin
  const handleSessionEnded = (data) => {
    // Show modal to user
    setShowSessionEndModal(true);

    // Clear chat immediately
    setChatId(null);
    setMessages([
      {
        id: 1,
        text: `Zdravo ${
          user?.username || user?.name || ""
        }! Dobrodošli u korisničku podršku. Kako možemo da vam pomognemo danas?`,
        sender: "support",
        timestamp: new Date(),
      },
    ]);
  };

  const handleCloseSessionEndModal = () => {
    setShowSessionEndModal(false);
    // Close chat and reset messages
    onClose();
  };

  const handleSendMessage = async () => {
    if (!message.trim()) return;

    // Don't add message locally - let it come from server to avoid duplicates
    const messageText = message;
    setMessage("");

    try {
      if (!chatId) {
        // First message - start new chat session
        supportChatService.startChatSession(messageText);
      } else {
        // Send message in existing session
        supportChatService.sendMessage(messageText);
      }

      // Stop typing after sending message
      supportChatService.sendTyping(false);
    } catch (error) {
      console.error("Failed to send message:", error);
      setConnectionError("Greška pri slanju poruke. Pokušajte ponovo.");
      // If error, restore the message text
      setMessage(messageText);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (date) => {
    return new Intl.DateTimeFormat("sr-Latn-RS", {
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  if (!isOpen) return null;

  return (
    <ChatContainer $isMinimized={isMinimized}>
      <ChatHeader $isMinimized={isMinimized}>
        <ChatTitle>
          <FiMessageCircle />
          Korisnička Podrška
          <StatusIndicator $online={adminOnline && isConnected}>
            ● {adminOnline && isConnected ? "Online" : "Offline"}
          </StatusIndicator>
        </ChatTitle>
        <ChatControls>
          <ChatButton onClick={onMinimize}>
            {isMinimized ? <FiMaximize2 /> : <FiMinimize2 />}
          </ChatButton>
          <ChatButton onClick={onClose}>
            <FiX />
          </ChatButton>
        </ChatControls>
      </ChatHeader>

      {!isMinimized && (
        <>
          <ChatBody>
            <MessagesContainer>
              {messages.map((msg) => (
                <Message key={msg.id} $isUser={msg.sender === "user"}>
                  <MessageSender>
                    <FiUser />
                    {msg.sender === "user" ? user?.username : "Podrška"}
                  </MessageSender>
                  <MessageContent $isUser={msg.sender === "user"}>
                    {msg.text}
                  </MessageContent>
                  <MessageTime>{formatTime(msg.timestamp)}</MessageTime>
                </Message>
              ))}
              {isTyping && (
                <TypingIndicator>
                  <div className="typing-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </TypingIndicator>
              )}
              <div ref={messagesEndRef} />
            </MessagesContainer>
          </ChatBody>

          <ChatFooter>
            <InputContainer>
              <ChatInput
                ref={inputRef}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                onFocus={() => {
                  supportChatService.sendTyping(true);
                }}
                onBlur={() => {
                  supportChatService.sendTyping(false);
                }}
                placeholder="Ukucajte vašu poruku..."
                rows="1"
              />
              <SendButton
                onClick={handleSendMessage}
                disabled={!message.trim()}
              >
                <FiSend size={14} />
              </SendButton>
            </InputContainer>
            <WelcomeMessage>
              {isConnected
                ? adminOnline
                  ? "Povezani ste sa support timom 24/7"
                  : "Offline mode - poruke se šalju na podrska@mrc.rs"
                : "Pokušavanje povezivanja..."}
              {connectionError && (
                <ErrorMessage>{connectionError}</ErrorMessage>
              )}
            </WelcomeMessage>
          </ChatFooter>
        </>
      )}

      {/* Session End Modal */}
      {showSessionEndModal && (
        <ModalOverlay>
          <ConfirmBox>
            <CloseBtn onClick={handleCloseSessionEndModal}>
              <FiX />
            </CloseBtn>
            <h3>Sesija završena</h3>
            <p>
              Admin je završio chat sesiju. Hvala vam što ste koristili našu
              podršku!
            </p>
            <ModalActions>
              <Confirm onClick={handleCloseSessionEndModal}>U redu</Confirm>
            </ModalActions>
          </ConfirmBox>
        </ModalOverlay>
      )}
    </ChatContainer>
  );
};

export default SupportChat;
